"use client";

import {
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
} from "recharts";

export type RadarDatum = {
  axis: string;
  value: number;
};

// Briefing-band palette so the radar's colour agrees with the headline label
// the user just read, rather than always rendering emerald.
function radarColour(score: number): string {
  if (score >= 8.5) return "#059669";
  if (score >= 7.0) return "#0d9488";
  if (score >= 5.0) return "#d97706";
  if (score >= 3.0) return "#ea580c";
  return "#dc2626";
}

export function ConditionsRadar({
  scores,
  assessment,
  score,
}: {
  scores: RadarDatum[];
  assessment?: string;
  /** Headline score to colour the radar by; defaults to the mean axis value. */
  score?: number;
}) {
  const meanAxis =
    scores.length > 0
      ? scores.reduce((sum, d) => sum + d.value, 0) / scores.length
      : 0;
  const colour = radarColour(score ?? meanAxis);
  return (
    <div>
      <div className="text-[12px] font-semibold uppercase tracking-[1.2px] text-text-muted mb-3">
        Conditions radar
      </div>
      <ResponsiveContainer
        width="100%"
        height={280}
        initialDimension={{ width: 720, height: 280 }}
      >
        <RadarChart data={scores} cx="50%" cy="50%" outerRadius="70%">
          <PolarGrid
            gridType="polygon"
            radialLines={false}
            stroke="#e5e7eb"
          />
          <PolarAngleAxis
            dataKey="axis"
            tick={{ fontSize: 11, fill: "#6b7280" }}
          />
          <PolarRadiusAxis
            domain={[0, 10]}
            tickCount={4}
            tick={{ fontSize: 9, fill: "#9ca3af" }}
            axisLine={false}
          />
          <Radar
            dataKey="value"
            stroke={colour}
            strokeWidth={2}
            fill={colour}
            fillOpacity={0.2}
            dot={{ r: 3, fill: colour, stroke: "#fff", strokeWidth: 1 }}
          />
        </RadarChart>
      </ResponsiveContainer>
      {assessment && (
        <p className="text-[13px] text-text-body text-center mt-2">
          {assessment}
        </p>
      )}
    </div>
  );
}

export function clampScore(v: number): number {
  return Math.round(Math.max(0, Math.min(10, v)) * 10) / 10;
}
